/* eslint-disable max-len */
import React from "react";
import getConfig from "next/config";

import styles from "./articleSocial.module.scss";

const { publicRuntimeConfig: { museHost } } = getConfig();

const getFaceBookUrl = (articleUri: string) => {
    const base = "https://www.facebook.com/sharer/sharer.php?u=";
    const article = encodeURIComponent(`${museHost}/${articleUri}`);
    return `${base}${article}`;
};

const getTwitterUrl = (articleUri: string, title: string, twitterHandle: string) => {
    const base = "https://twitter.com/share?url=";
    const heading = `&text=${encodeURIComponent(title)}`;
    const signature = twitterHandle === "themuse" ? "%20%40TheMuse" : `%20%40TheMuse%20%40${twitterHandle}`;
    const article = encodeURIComponent(`${museHost}/${articleUri}`);
    return `${base}${article}${heading}${signature}`;
};

const getLinkedInUrl = (articleUri: string, title: string) => {
    const base = "http://www.linkedin.com/shareArticle?mini=true&url=";
    const heading = "&source=The%20Muse&title=";
    return `${base}${museHost}/${articleUri}${heading}${title.split(" ").join("+")}`;
};

const getEmailUrl = (articleUri: string, title: string) => {
    const subject = `mailto:?Subject=${title}`;
    const body = "&body=Just read this awesome article I thought I would share with you! Check it out here: ";
    const url = `${subject}${body}$${museHost}/${articleUri}`;
    return url;
};

export interface ISocialSidebarProps {
    articleUri: string;
    title: string;
    twitterHandle: string;
    id?: number;
    writerName: string;
    writerSlug: string;
    dateUpdated: string;
}

interface IconProps {
    url: string;
    type: string;
}

const facebookIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        aria-hidden="false"
        role="img"
        aria-labelledby="facebook-social"
        aria-label="Share on Facebook"
        height="1em"
        width="1em"
    >
        <title id="facebook-social">Facebook</title>
        <path fill="#FFF" d="M9 8H6v4h3v12h5V12h3.642L18 8h-4V6.333C14 5.378 14.192 5 15.115 5H18V0h-3.808C10.596 0 9 1.583 9 4.615V8z" />
    </svg>
);

const linkedinIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        aria-hidden="false"
        role="img"
        aria-labelledby="linkedin-social"
        aria-label="Share on LinkedIn"
        height="1em"
        width="1em"
    >
        <title id="linkedin-social">LinkedIn</title>
        <path fill="#FFF" d="M4.98 3.5C4.98 4.881 3.87 6 2.5 6S.02 4.881.02 3.5C.02 2.12 1.13 1 2.5 1s2.48 1.12 2.48 2.5zM5 8H0v16h5V8zm7.982 0H8.014v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0V24H24V13.869c0-7.88-8.922-7.593-11.018-3.714V8z" />
    </svg>
);

const twitterIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        aria-hidden="false"
        role="img"
        aria-labelledby="twitter-social"
        aria-label="Share on Twitter"
        height="1em"
        width="1em"
    >
        <title id="twitter-social">Twitter</title>
        <path fill="#FFF" d="M24 4.557a9.83 9.83 0 01-2.828.775 4.932 4.932 0 002.165-2.724 9.864 9.864 0 01-3.127 1.195 4.916 4.916 0 00-3.594-1.555c-3.179 0-5.515 2.966-4.797 6.045A13.978 13.978 0 011.671 3.149a4.93 4.93 0 001.523 6.574 4.903 4.903 0 01-2.229-.616c-.054 2.281 1.581 4.415 3.949 4.89a4.935 4.935 0 01-2.224.084 4.928 4.928 0 004.6 3.419A9.9 9.9 0 010 19.54a13.94 13.94 0 007.548 2.212c9.142 0 14.307-7.721 13.995-14.646A10.025 10.025 0 0024 4.557z" />
    </svg>
);

const emailIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        aria-hidden="false"
        role="img"
        aria-labelledby="email-social"
        aria-label="Share via Email"
        height="1em"
        width="1em"
    >
        <title id="email-social">Email</title>
        <path fill="#FFF" d="M29.728 26.848v-13.696q-0.576 0.64-1.248 1.152-4.768 3.68-7.616 6.048-0.896 0.768-1.472 1.184t-1.536 0.896-1.824 0.416h-0.064q-0.832 0-1.824-0.416t-1.536-0.896-1.472-1.184q-2.816-2.368-7.616-6.048-0.672-0.512-1.248-1.152v13.696q0 0.224 0.192 0.416t0.384 0.16h26.304q0.224 0 0.384-0.16t0.192-0.416zM29.728 8.096v-0.448t-0.032-0.224-0.032-0.224-0.096-0.16-0.16-0.128-0.256-0.064h-26.304q-0.224 0-0.384 0.192t-0.192 0.384q0 3.008 2.656 5.088 3.424 2.688 7.136 5.632 0.128 0.096 0.64 0.544t0.8 0.672 0.8 0.544 0.896 0.512 0.768 0.16h0.064q0.352 0 0.768-0.16t0.896-0.512 0.8-0.544 0.8-0.672 0.64-0.544q3.712-2.944 7.168-5.632 0.96-0.768 1.792-2.080t0.832-2.336zM32 7.424v19.424q0 1.184-0.832 2.016t-2.016 0.864h-26.304q-1.152 0-2.016-0.864t-0.832-2.016v-19.424q0-1.184 0.832-2.016t2.016-0.832h26.304q1.184 0 2.016 0.832t0.832 2.016z" />
    </svg>
);

/**
 * Generates an anchor wrapped around the social icon.
 */
const getIcon: React.FC<IconProps> = (props) => {
    const { url, type } = props;

    type socialKeys = "facebook" | "twitter" | "linkedin" | "email";
    type socialIconsMap = { [type in socialKeys]: React.FC };

    const socialIcons: socialIconsMap = {
        facebook: facebookIcon,
        twitter: twitterIcon,
        linkedin: linkedinIcon,
        email: emailIcon
    };

    const Icon = socialIcons[type as socialKeys];

    return (
        <a
            className={`${styles.icon} ${styles[type]}`}
            aria-label={type}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
        >
            <Icon />
        </a>
    );
};

/**
 * Generates the Social Sidebar Module.  This includes the Author Byline and the 4 social icons.
 */
export const ArticleSocial: React.FC<ISocialSidebarProps> = (props) => {
    const {
        articleUri, title, twitterHandle, writerName, writerSlug, dateUpdated
    } = props;
    const handle = twitterHandle ? twitterHandle.replace("@", "") : "themuse";
    const emailUrl = getEmailUrl(articleUri, title);
    const faceBookUrl = getFaceBookUrl(articleUri);
    const twitterUrl = getTwitterUrl(articleUri, title, handle);
    const linkedInUrl = getLinkedInUrl(articleUri, title);

    const writerUri = `/author/${writerSlug}`;

    return (
        <aside id="social-marker" className={styles.social}>
            <div className={styles.articleMetadata}>
                <div className={styles.dataGroup}>
                    <div className={styles.metadataField}>by</div>
                    <div className={styles.metadataData}>
                        <a href={writerUri}>
                            {writerName}
                        </a>
                    </div>
                </div>
                <div className={styles.dataGroup}>
                    <div className={styles.metadataField}>Updated</div>
                    <div className={styles.metadataData}>
                        {dateUpdated}
                    </div>
                </div>
            </div>
            {getIcon({ ...{ url: faceBookUrl, type: "facebook" } })}
            {getIcon({ ...{ url: twitterUrl, type: "twitter" } })}
            {getIcon({ ...{ url: linkedInUrl, type: "linkedin" } })}
            {getIcon({ ...{ url: emailUrl, type: "email" } })}
        </aside>
    );
};
